import React, { useContext } from 'react';

import { Star } from 'lucide-react';

import Colors, { type ColorKeys, type ColorNames } from 'common/colors/constants';
import { type HexColor, colorPaletteHash } from 'common/colors/utils';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { TintColorContext } from 'common/containers/TintColorContainer';
import { cn } from 'common/style/utils';
import Avatar from 'common/uiV3/Avatar';

import type { StrippedUserWithAlias } from 'common/api/endpoints/users';
import type { VariantProps } from 'tailwind-variants';

const DefaultID = '1';

interface OwnProps extends VariantProps<typeof Avatar> {
  user: Omit<StrippedUserWithAlias, 'created' | 'urlName'>;
  color?: ColorNames;
  alt?: string;
  showBadge?: boolean;
}

const UserAvatarV2 = ({ user, alt, size, shape, showBadge = false }: OwnProps) => {
  const tintColor = useContext(TintColorContext);
  const company = useContext(CompanyContext);

  const _id = user._id ?? user.aliasID ?? DefaultID;
  const avatarColor = colorPaletteHash(_id);
  const initial = user.name.substring(0, 1).toUpperCase();
  const shouldShowBadge = showBadge && company?.members?.some((member) => member._id === user._id);

  return (
    <div className="relative">
      <Avatar size={size} shape={shape}>
        <Avatar.Image src={user.avatarURL || ''} alt={alt || user.name} />
        <Avatar.Fallback
          style={{
            color: Colors[`${avatarColor}110` as ColorKeys],
            backgroundColor: Colors[`${avatarColor}40` as ColorKeys],
          }}>
          {initial}
        </Avatar.Fallback>
        {shouldShowBadge && <StarBadge tintColor={tintColor} />}
      </Avatar>
    </div>
  );
};

const StarBadge = ({ tintColor }: { tintColor: HexColor | null }) => {
  return (
    <div
      className={cn(
        'box-border absolute -bottom-0.5 -right-0.5',
        'flex items-center justify-center',
        'size-3.5 rounded-full',
        'border-2 border-white',
        'bg-primary',
      )}
      style={{
        ...(tintColor ? { backgroundColor: tintColor } : undefined),
      }}>
      <Star className="size-2 fill-primary-foreground text-primary-foreground" />
    </div>
  );
};

export default UserAvatarV2;
